import { type ChangeEvent, type FormEvent, useState } from 'react';

import { useValidateInputType } from '@virginexperiencedays/hooks';
import { cn } from '@virginexperiencedays/tailwind';

import { BodyText } from '../../typography/BodyText';
import { DisplayHeading } from '../../typography/DisplayHeading';

import { Button } from '../Button';

export type NewsletterProps = {
  onSubmit: ({ email }: { email: string }, signUpSource: string) => void;
  signUpSource: string;
  className?: string;
  onChange?: ({ email }: { email: string }) => void;
  roundedLeftButton?: boolean;
};

export const Newsletter = ({
  className,
  onSubmit,
  signUpSource,
  onChange,
  roundedLeftButton = true,
}: NewsletterProps) => {
  const { value: email, valid, handleChange, validate } = useValidateInputType('email');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    try {
      if (!validate()?.isValid) return;
      onSubmit({ email }, signUpSource);
      setIsSubmitted(true);
    } catch (error) {
      console.error(error instanceof Error ? error.message : 'Error submitting newsletter');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {!isSubmitted ? (
        <div
          className={cn(
            'my-10 flex flex-col items-center justify-center gap-4 text-center md:mx-auto md:max-w-[522px]',
            className,
          )}
        >
          <DisplayHeading className="text-neutral" size="3" as="h2">
            Keep me updated about new experiences and offers from Virgin Experience Days.
          </DisplayHeading>
          <div className="mt-4 flex w-full justify-center">
            <input
              className={cn(
                'border-border-neutral font-inter h-12 w-full shrink grow-0 basis-full appearance-none rounded-l-lg border border-r-0 p-4 text-sm',
                valid.isValid === false && 'border-border-primary',
              )}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                handleChange(e.target.value);
                onChange?.({ email });
              }}
              placeholder="Your email address"
              type="email"
              value={email}
              required
            />
            <Button
              className={cn(
                'border-border-neutral shrink grow-0 basis-1/2 rounded-r-lg border md:basis-2/5',
                valid.isValid === false && 'border-border-primary',
                !roundedLeftButton && 'rounded-l-none',
              )}
              variant="primary"
              onClick={handleSubmit}
              type="submit"
            >
              Sign Up
            </Button>
          </div>
          <BodyText className="text-brand text-center" size="small">
            {valid.isValid === false && ' Please enter a valid email address'}
          </BodyText>
          <BodyText className="text-neutral text-center" size="small">
            By entering your email address you agree to receive emails from Virgin Experience Days
            about our latest experiences, news, and deals.
          </BodyText>
        </div>
      ) : (
        <div className="flex min-h-[249px] flex-col items-center justify-center md:min-h-[186px]">
          <DisplayHeading className="text-strong mb-4" size="3">
            Thanks for signing up!
          </DisplayHeading>
          <BodyText className="text-neutral text-center" size="small">
            Look out for us in your inbox soon
          </BodyText>
        </div>
      )}
    </form>
  );
};
